/**
 * 展会列表 Nuxt Island 为纯 SSR 静态 HTML（无 hydration），
 * 交互须用 inline onclick + window 全局函数（同 __handleOnlineService）。
 */
import type { NuxtApp } from '#app'
import type { Ref } from 'vue'
import { watch } from 'vue'
import { fetchExhibitionFollow } from '~~/server/api/exhibition'
import { ResponseCode } from '~/utils/constant'
import { useGlobalStore } from '~/stores/global'
import { useUserStore } from '~/stores/user'

declare global {
  interface Window {
    __handleFairListSubscribe?: (exhibitionId: string, btn?: HTMLElement | null) => void
    __openLoginModal?: () => void
  }
}

function setSubscribeButtonFocus(btn: HTMLElement, isFocus: boolean) {
  const subscribeLabel = btn.getAttribute('data-subscribe-label') || '订阅'
  const subscribedLabel = btn.getAttribute('data-subscribed-label') || '已订阅'
  btn.textContent = isFocus ? subscribedLabel : subscribeLabel
  btn.setAttribute('data-is-focus', isFocus ? '1' : '0')
}

/** Pinia 中的 loginModal 可能是 Ref，也可能已被 unwrap 为组件实例 */
function openLoginModal() {
  if (typeof window.__openLoginModal === 'function') {
    window.__openLoginModal()
    return
  }

  const globalStore = useGlobalStore()
  const modal = globalStore.loginModal as Ref<{ open?: () => void } | null> | { open?: () => void } | null
  if (!modal) return

  if (typeof modal === 'object' && modal !== null && 'value' in modal) {
    modal.value?.open?.()
    return
  }

  modal.open?.()
}

async function performFairListSubscribe(nuxtApp: NuxtApp, exhibitionId: string, btn: HTMLElement | null) {
  if (!exhibitionId) return

  await nuxtApp.runWithContext(async () => {
    const targetBtn =
      btn ??
      (document.querySelector(
        `[data-fair-list-subscribe-btn][data-exhibition-id="${CSS.escape(exhibitionId)}"]`
      ) as HTMLElement | null)

    if (!targetBtn) return

    const token = useCookie('access_token')
    const globalStore = useGlobalStore()

    if (!token.value && !globalStore.isLogin) {
      openLoginModal()
      return
    }

    const userStore = useUserStore()
    const industryId = targetBtn.getAttribute('data-industry-id')
    userStore.setOrigins(`${location.origin}${location.pathname}${location.search}`)
    userStore.setAction(4)
    if (industryId) {
      userStore.setIndustryId(Number(industryId) || 0)
    }

    const res = await fetchExhibitionFollow({ exhibitionId })
    if (res?.code !== ResponseCode.SUCCESS) return

    const nextFocus = targetBtn.getAttribute('data-is-focus') !== '1'
    setSubscribeButtonFocus(targetBtn, nextFocus)

    const rows = useState<any[]>('fairListExhibitionClientRows', () => [])
    if (Array.isArray(rows.value) && rows.value.length) {
      rows.value = rows.value.map((row: any) =>
        String(row.exhibitionId ?? row.id) === exhibitionId ? { ...row, isFocus: nextFocus } : row
      )
    }
  })
}

export default defineNuxtPlugin(nuxtApp => {
  window.__handleFairListSubscribe = (exhibitionId, btn) => {
    void performFairListSubscribe(nuxtApp, exhibitionId, btn ?? null)
  }

  // 登录态同步：退出登录后将列表 SSR 订阅按钮还原为“订阅”
  nuxtApp.runWithContext(() => {
    const globalStore = useGlobalStore()
    watch(
      () => globalStore.isLogin,
      (v, old) => {
        if (!(old && !v)) return
        document
          .querySelectorAll<HTMLElement>('[data-fair-list-subscribe-btn][data-is-focus="1"]')
          .forEach(btn => setSubscribeButtonFocus(btn, false))
        const rows = useState<any[]>('fairListExhibitionClientRows', () => [])
        if (Array.isArray(rows.value) && rows.value.length) {
          rows.value = rows.value.map((row: any) => ({ ...row, isFocus: false }))
        }
      }
    )
  })
})
